/* eslint-disable no-unused-vars */
// eslint-disable-next-line import/no-unused-modules
import React, { useMemo } from 'react';
import { map } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
    getConditionStatus,
    getFormatedAlarmAlertLimits,
    getFormattedAlarmAlertLines,
    getCondtionTrendIndicators,
    getHighErrorThresholdValue,
    getHighAlertThresholdValue,
    getLowAlertThresholdValue,
    getLowErrorThresholdValue,
    getValueType
} from './helpers';
import KPIListChartListItem from './KPITrendChartListItem';
import DeferredChart from './DeferredChart';

const DEFAULT_NO_FORECAST_WINDOW_MS = 30 * 24 * 3600 * 1000;

const KPITrendChartListComponent = (props) => {
    const { t: translate } = useTranslation();

    // Compute sharedForecastSeparatorX (max endTime) and sharedXAxisMax (separator + longest forecast duration) for uniform x-axis alignment across all charts.
    const { sharedForecastSeparatorX, sharedXAxisMax } = useMemo(() => {
        if (!props?.showConditionForecast || props?.isZoomed) {
            return { sharedForecastSeparatorX: null, sharedXAxisMax: null };
        }
        let maxEndTs = null;
        let maxForecastDuration = 0;

        props?.trendParams?.forEach(chartItem => {
            // Shared separator from endTime (represents when each KPI's data window closes)
            const endTs = chartItem?.endTime ? new Date(chartItem.endTime).getTime() : null;
            if (endTs && !Number.isNaN(endTs) && (maxEndTs === null || endTs > maxEndTs)) {
                maxEndTs = endTs;
            }

            // Compute the duration of this KPI's forecast data
            const forecast = chartItem?.conditionForecast;
            if (forecast?.length) {
                const validTs = forecast
                    .map(item => new Date(item?.timestamp).getTime())
                    .filter(ts => !Number.isNaN(ts))
                    .sort((a, b) => a - b);
                if (validTs.length >= 2) {
                    const duration = validTs[validTs.length - 1] - validTs[0];
                    if (duration > maxForecastDuration) {
                        maxForecastDuration = duration;
                    }
                }
            }
        });

        const alignedMax = maxEndTs === null ? null : maxEndTs + (maxForecastDuration || DEFAULT_NO_FORECAST_WINDOW_MS);

        return { sharedForecastSeparatorX: maxEndTs, sharedXAxisMax: alignedMax };
    }, [props?.showConditionForecast, props?.isZoomed, props?.trendParams]);

    const totalKPIItems = useMemo(() => map(props?.trendParams, (chartItem) => {
        // Hide forecast completely when zoomed — only show it in the unzoomed state
        const kpiForecast = props?.showConditionForecast && !props?.isZoomed
            ? chartItem?.conditionForecast || []
            : null;
        return (
            <div
                className='conditional-kpi-section-chart-item'
                id={chartItem?.key}
                key={chartItem?.key}
            >
                <DeferredChart>
                    <KPIListChartListItem
                        model={{
                            data: chartItem?.trend,
                            key: chartItem?.key,
                            from: '',
                            to: '',
                            alertAlarmLimitBackgrounds:
                                getFormatedAlarmAlertLimits(
                                    chartItem?.conditionTrends
                                ),
                            alertAlarmLimitLines: getFormattedAlarmAlertLines(
                                chartItem?.conditionTrends
                            ),
                            conditionIndicators: getCondtionTrendIndicators(
                                chartItem?.conditionEvents
                            ),
                            title: translate(chartItem?.nameTextKey) || chartItem?.name,
                            nameTextKey: chartItem?.nameTextKey,
                            loadingStatus: props?.loadingStatus,
                            conditionStatus: getConditionStatus(
                                chartItem?.currentCondition
                            ),
                            zoom: props?.isZoomed,
                            unit: chartItem?.unit,
                            highErrorValue: getHighErrorThresholdValue(
                                chartItem?.conditionTrends
                            ),
                            highAlertLimitValue: getHighAlertThresholdValue(
                                chartItem?.conditionTrends
                            ),
                            lowErrorValue: getLowErrorThresholdValue(chartItem?.conditionTrends),
                            lowAlertValue: getLowAlertThresholdValue(chartItem?.conditionTrends),
                            unitGroupId: chartItem?.unitGroupId,
                            valueType: translate(getValueType(chartItem?.valueType?.toLowerCase())),
                            conditionTrendsData: chartItem?.conditionTrends,
                            conditionForecast: kpiForecast,
                            sharedXAxisMax: sharedXAxisMax,
                            sharedForecastSeparatorX: sharedForecastSeparatorX,
                        }}
                        data={chartItem?.trend}
                        key={chartItem?.key}
                        name={chartItem?.name}
                        loadingStatus={props?.loadingStatus}
                        unit={chartItem?.unit}
                        currentCondition={chartItem?.currentCondition}
                        actions={props?.actions}
                        assetId={props?.assetId}
                        assetDetails={props?.assetDetails}
                    ></KPIListChartListItem>
                </DeferredChart>
            </div>
        );
    }), [
        props?.trendParams,
        props?.showConditionForecast,
        props?.isZoomed,
        props?.loadingStatus,
        props?.assetId,
        props?.assetDetails,
        props?.actions,
        sharedXAxisMax,
        sharedForecastSeparatorX,
        translate
    ]);

    return (
        <div className='conditional-kpi-section-chart-list'>
            {totalKPIItems}
        </div>
    );
};


// eslint-disable-next-line import/no-unused-modules
export default KPITrendChartListComponent;
